import React, { useMemo, useState } from 'react';
import {
  Avatar,
  AvatarFallback,
  Button,
  Select,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  DeleteConfirmationModal,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useContactsDropdown } from '@/modules/contacts/hooks';
import { useAssociateCustomerContact, useDisassociateCustomerContact } from '../hooks';

/** Iniciais do nome do contato (2 letras) — avatar sem foto = slate (AvatarFallback). */
function getContactInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

export interface CustomerContactsCardProps {
  customerId: string;
  /** `customer.contacts` — ids dos contatos vinculados. */
  contactIds: string[];
  /** Gate de `customers.update` — controla exibir "Adicionar"/remover. */
  canManage: boolean;
}

/**
 * Card "Contatos vinculados" do detalhe do cliente — lista os contatos
 * associados (nome + telefone/email, resolvidos via `useContactsDropdown`
 * cross-module) e permite associar (Select + `POST /customers/:id/contacts`)
 * ou desvincular (`DELETE /customers/:id/contacts/:contactId`) um contato,
 * gated por `canManage` (permissão `customers.update`).
 *
 * Anatomia alinhada ao mockup `crm-customer-detail.html` (card com header
 * título + contagem + ação, linhas "deal" com avatar slate). Tokens do tema
 * na página; modais permanecem em tema claro fixo (shared components).
 */
export const CustomerContactsCard: React.FC<CustomerContactsCardProps> = ({
  customerId,
  contactIds,
  canManage,
}) => {
  const [isAssociateOpen, setIsAssociateOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [contactPendingRemoval, setContactPendingRemoval] = useState<{ id: string; name: string } | null>(null);

  // Lookup cross-module leve (id/name/phone/email) — mesmo padrão de
  // `ProjectsPage` (useUsersDropdown não gated, página real e não modal).
  const { data: allContacts, isLoading: isLoadingContacts } = useContactsDropdown();

  const associateMutation = useAssociateCustomerContact();
  const disassociateMutation = useDisassociateCustomerContact();

  const contactsById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; phone?: string; email?: string }>();
    (allContacts ?? []).forEach((c) => map.set(c.id, c));
    return map;
  }, [allContacts]);

  const linkedContacts = contactIds.map((id) => contactsById.get(id) ?? { id, name: 'Contato não encontrado' });

  const availableOptions = useMemo(
    () =>
      (allContacts ?? [])
        .filter((c) => !contactIds.includes(c.id))
        .map((c) => ({ value: c.id, label: c.phone || c.email ? `${c.name} (${c.phone || c.email})` : c.name })),
    [allContacts, contactIds],
  );

  const handleAssociate = () => {
    if (!selectedContactId) return;
    associateMutation.mutate(
      { customerId, contactId: selectedContactId },
      {
        onSuccess: () => {
          setIsAssociateOpen(false);
          setSelectedContactId('');
        },
      },
    );
  };

  const handleConfirmRemoval = () => {
    if (!contactPendingRemoval) return;
    disassociateMutation.mutate(
      { customerId, contactId: contactPendingRemoval.id },
      { onSuccess: () => setContactPendingRemoval(null) },
    );
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
      data-testid="customers-contacts-card"
    >
      {/* Header: título + contagem + ação */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid var(--theme-card-border)' }}
      >
        <h2 className="text-sm font-bold flex items-center gap-1.5 min-w-0" style={{ color: 'var(--theme-card-text)' }}>
          <span className="truncate">Contatos vinculados</span>
          <span className="text-xs font-medium flex-none" style={{ color: 'var(--theme-muted-text)' }}>
            · {contactIds.length}
          </span>
        </h2>
        {canManage && (
          <Button
            size="sm"
            variant="secondary"
            icon="plus"
            onClick={() => setIsAssociateOpen(true)}
            data-testid="customers-contacts-add"
          >
            Adicionar
          </Button>
        )}
      </div>

      {/* Corpo: linhas de contato */}
      {contactIds.length === 0 ? (
        <p className="text-sm px-4 py-4" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhum contato vinculado a este cliente.
        </p>
      ) : (
        linkedContacts.map((contact, index) => {
          const detail = 'phone' in contact ? contact.phone || contact.email : undefined;
          return (
            <div
              key={contact.id}
              className="flex items-center gap-3 px-4 py-3"
              style={index > 0 ? { borderTop: '1px solid var(--theme-card-border)' } : undefined}
              data-testid={`customers-contacts-item-${contact.id}`}
            >
              {/* F83/F44: contato = pessoa → avatar squircle 36, fallback slate com iniciais */}
              <Avatar className="w-9 h-9 flex-none" isSquircle>
                <AvatarFallback isSquircle>
                  <span className="text-white font-semibold text-xs">{getContactInitials(contact.name)}</span>
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {contact.name}
                </p>
                {detail && (
                  <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                    {detail}
                  </p>
                )}
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => setContactPendingRemoval({ id: contact.id, name: contact.name })}
                  className="p-1.5 rounded-lg transition-colors flex-shrink-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  title="Desvincular contato"
                  data-testid={`customers-contacts-remove-${contact.id}`}
                >
                  <Icon name="x" className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })
      )}

      {/* Modal: associar contato */}
      <Modal isOpen={isAssociateOpen} onClose={() => setIsAssociateOpen(false)} maxWidth="sm">
        <ModalHeader onClose={() => setIsAssociateOpen(false)}>
          <h2 className="text-base font-semibold" style={{ color: '#1e293b' }}>
            Vincular contato
          </h2>
        </ModalHeader>
        <ModalContent>
          <Select
            label="Contato"
            placeholder={isLoadingContacts ? 'Carregando contatos...' : 'Selecione um contato'}
            options={availableOptions}
            value={selectedContactId}
            onChange={setSelectedContactId}
            disabled={isLoadingContacts}
            data-testid="customers-contacts-select"
          />
          {!isLoadingContacts && availableOptions.length === 0 && (
            <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
              Todos os contatos já estão vinculados a este cliente.
            </p>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsAssociateOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleAssociate}
            disabled={!selectedContactId || associateMutation.isPending}
            loading={associateMutation.isPending}
            data-testid="customers-contacts-confirm"
          >
            Vincular
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação: desvincular contato */}
      <DeleteConfirmationModal
        isOpen={!!contactPendingRemoval}
        onClose={() => setContactPendingRemoval(null)}
        onConfirm={handleConfirmRemoval}
        variant="default"
        title="Desvincular contato"
        message={`Tem certeza que deseja desvincular "${contactPendingRemoval?.name}" deste cliente?`}
        confirmButtonText="Desvincular"
        loadingText="Desvinculando..."
        isLoading={disassociateMutation.isPending}
      />
    </div>
  );
};

CustomerContactsCard.displayName = 'CustomerContactsCard';
